/**
 * Landlord claim flow — client helpers (landlord side, Piotr).
 *
 * Kept as a standalone module (not folded into shared onboarding files) so the
 * claim feature owns its own logic and the edits to the shared "shell"
 * (App.tsx, BasicInfoStep, OnboardingRouter) stay minimal + additive — cheap to
 * rebase onto Maciek's main. See [[flat_hero_collaboration_model]].
 *
 * Flow: a /claim/:token landing page stashes the token (ClaimCapture, mirrors
 * InviteCapture) → the visitor signs up → BasicInfoStep presets role='owner' →
 * after SMS-verify, OnboardingRouter redeems the claim (claim_source_post runs
 * as auth.uid(), so it must be called from the authed client, not service role).
 */

import { supabase } from '@/integrations/supabase/client';
import { isListingLimitError } from '@/lib/listingLimit';
import { clearFlowToken, readFlowToken, stashFlowToken } from '@/lib/flowToken';

const CLAIM_TOKEN_KEY = 'flathero_claim_token';

/** A claim token looks like 36 hex chars (`encode(gen_random_bytes(18),'hex')`). */
export function isClaimToken(token: string | null | undefined): token is string {
  return !!token && /^[0-9a-f]{36}$/i.test(token);
}

/**
 * Stash the token for the signup → onboarding round-trip. Uses localStorage (not
 * sessionStorage): the Supabase email-OTP signup hop could drop sessionStorage,
 * leaving OnboardingRouter with no token → the claim stayed 'invited' and the owner
 * hit the coming-soon wall. localStorage survives the hop; cleared on redeem.
 *
 * Bounded by FLOW_TOKEN_TTL_MS — an unbounded stash hijacked a later, unrelated
 * signup (2026-07-30). Calling this again restarts the clock.
 */
export function stashClaimToken(token: string): void {
  if (isClaimToken(token)) stashFlowToken(CLAIM_TOKEN_KEY, token);
}

export function getPendingClaimToken(): string | null {
  return readFlowToken(CLAIM_TOKEN_KEY, isClaimToken);
}

export function clearPendingClaimToken(): void {
  clearFlowToken(CLAIM_TOKEN_KEY);
}

/**
 * Stamp "this identity came in for this flat" server-side, at the moment the
 * account is created (BasicInfoStep, right after the `users` row lands).
 *
 * The browser stash is bounded by FLOW_TOKEN_TTL_MS = 2 h — deliberately, since
 * an unbounded one hijacked an unrelated signup (#61). But the usual claim
 * signup stalls at the SMS code and resumes the next day, long past that window,
 * and until now the link died with the stash even though the claim was still
 * open server-side. Fire-and-forget: a failed stamp must never block signup.
 */
export async function markClaimSignup(token: string): Promise<void> {
  if (!isClaimToken(token)) return;
  try {
    await supabase
      .schema('flat' as never)
      .rpc('mark_claim_signup' as never, { p_token: token } as never);
  } catch {
    /* best-effort: the browser stash still covers the same-sitting path */
  }
}

/**
 * The token to redeem with, browser first and server second.
 *
 * `resume_claim_signup()` takes no argument — auth.uid() is its only input, so
 * it cannot be used to fish for someone else's token — and only returns one for
 * a claim that is STILL open (invited, unexpired). A claim taken by somebody
 * else, expired or declined never comes back: recovery must not be a side door
 * around those outcomes. Re-stashes so the existing redeem path works unchanged.
 */
export async function ensurePendingClaimToken(): Promise<string | null> {
  const local = getPendingClaimToken();
  if (local) return local;

  try {
    const { data, error } = await supabase
      .schema('flat' as never)
      .rpc('resume_claim_signup' as never);
    if (error) return null;
    const token = typeof data === 'string' ? data : null;
    if (!isClaimToken(token)) return null;
    stashClaimToken(token);
    return token;
  } catch {
    return null;
  }
}

export interface RedeemResult {
  redeemed: boolean;
  alreadyClaimed?: boolean;
  listingId?: string | null;
  listingCreated?: boolean;
  /** The claimed flat's `flat.offers.id` [D2] — lets /claim/success show its
   * demand, and seeds the wizard prefill. Stamped on the claim at redemption
   * (claim_source_post v3.5), so it is the same id before and after publish. */
  offerId?: string | null;
  error?: string;
}

/**
 * Redeem a pending claim for the currently-authenticated user. Calls the
 * SECURITY DEFINER RPC `flat.claim_source_post(p_token)` which keys off
 * auth.uid() (so a user can only claim as themselves) and, when the source post
 * has the required fields, creates the owner's `flat.listings` row.
 *
 * Clears the stashed token on a terminal outcome (claimed / already-claimed /
 * invalid) so we don't loop. Returns {redeemed:false} with no token present.
 */
export async function redeemPendingClaim(): Promise<RedeemResult> {
  const token = getPendingClaimToken();
  if (!token) return { redeemed: false };

  // flat schema is not in the generated Supabase types — opt out of typing
  // (same `as never` escape hatch used by the flat-schema hooks).
  const { data, error } = await supabase
    .schema('flat' as never)
    .rpc('claim_source_post' as never, { p_token: token } as never);

  if (error) {
    // One-listing limit (DB trigger inside claim_source_post's insert) is
    // TERMINAL — clear the token so login doesn't retry-loop; the caller
    // shows the "multi-listing coming soon" message.
    if (isListingLimitError(error.message)) {
      clearPendingClaimToken();
      return { redeemed: false, error: 'owner_listing_limit' };
    }
    // Transient (network/RLS) — keep the token so a later attempt can retry.
    return { redeemed: false, error: error.message };
  }

  const r = (data ?? {}) as {
    ok?: boolean;
    error?: string;
    already_claimed?: boolean;
    listing_id?: string | null;
    listing_created?: boolean;
    offer_id?: string | null;
  };

  if (r.ok && r.already_claimed) {
    clearPendingClaimToken();
    return {
      redeemed: true,
      alreadyClaimed: true,
      listingId: r.listing_id ?? null,
      offerId: r.offer_id ?? null,
    };
  }
  if (r.ok) {
    clearPendingClaimToken();
    return {
      redeemed: true,
      listingId: r.listing_id ?? null,
      listingCreated: !!r.listing_created,
      offerId: r.offer_id ?? null,
    };
  }

  // Terminal server-side rejection (invalid/expired/not-open/claimed-by-other
  // token) — drop it so we don't loop on every login.
  if (r.error && r.error !== 'not_authenticated') clearPendingClaimToken();
  return { redeemed: false, error: r.error };
}
